import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, Mail, Lock, Gem } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LoginAgeGateModal from '../components/LoginAgeGateModal';
import Button from '../components/ui/Button';
import { trackCompleteRegistration } from '../utils/metaPixel';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirect') || '/';
  const fromPayment = params.get('from') === 'payment';
  const emailFromPayment = params.get('email') || '';

  useEffect(() => {
    if (emailFromPayment) {
      setFormData((prev) => ({ ...prev, email: emailFromPayment }));
    }
  }, [emailFromPayment]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      setLoading(false);
      return;
    }

    const result = await register(formData.name, formData.email, formData.password);

    if (result.success) {
      trackCompleteRegistration();
      const paid = result.user?.hasPaid || result.user?.role === 'admin' || fromPayment;
      navigate(paid ? '/app' : redirectTo === '/' || !redirectTo ? '/' : redirectTo);
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 pt-20">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(600px circle at 80% 10%, rgba(225,29,72,0.10), transparent 60%), radial-gradient(500px circle at 15% 90%, rgba(225,29,72,0.08), transparent 60%)',
          }}
        />

        <div className="relative z-10 w-full max-w-md space-y-8 py-12">
          <div className="text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/5 p-2 ring-1 ring-white/10">
              <img src="/img/logo.png" alt="Logo" className="h-full w-full object-contain" />
            </div>
            <h1 className="text-2xl font-semibold text-white">Crie sua conta gratuita</h1>
            {fromPayment ? (
              <p className="mt-2 text-sm text-emerald-400">
                Pagamento confirmado! Cadastre-se com o mesmo email do PIX para liberar o app.
              </p>
            ) : (
              <p className="mt-2 text-sm text-zinc-400">
                Já tem uma conta?{' '}
                <Link to="/login" className="font-medium text-rose-400 hover:text-rose-300">
                  Fazer login
                </Link>
              </p>
            )}

            {redirectTo === '/tokens' && (
              <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
                <Gem className="h-4 w-4 shrink-0" />
                Depois do cadastro você poderá comprar tokens
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-white/10 bg-zinc-900 p-8 shadow-xl">
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-zinc-300">
                  Nome completo
                </label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    className="w-full rounded-lg border border-zinc-700 bg-zinc-800 py-2.5 pl-10 pr-4 text-white placeholder-zinc-500 transition-colors focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                    placeholder="Seu nome completo"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-zinc-300">
                  Email
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className="w-full rounded-lg border border-zinc-700 bg-zinc-800 py-2.5 pl-10 pr-4 text-white placeholder-zinc-500 transition-colors focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="password"
                    className="mb-1.5 block text-sm font-medium text-zinc-300"
                  >
                    Senha
                  </label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      autoComplete="new-password"
                      className="w-full rounded-lg border border-zinc-700 bg-zinc-800 py-2.5 pl-10 pr-3 text-white placeholder-zinc-500 transition-colors focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="mb-1.5 block text-sm font-medium text-zinc-300"
                  >
                    Confirmar
                  </label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required
                      autoComplete="new-password"
                      className="w-full rounded-lg border border-zinc-700 bg-zinc-800 py-2.5 pl-10 pr-3 text-white placeholder-zinc-500 transition-colors focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
                  {error}
                </div>
              )}

              <Button type="submit" fullWidth size="lg" loading={loading}>
                {loading ? 'Criando conta...' : 'Criar conta'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <LoginAgeGateModal />
    </div>
  );
};

export default Register;
